import {
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';

import Plan from '../models/Plan/Plan';
import planAPI from '../services/plan';
import { PaginationMetaLink } from '../models/Paginator';

// interfaces
interface PlanContextProps {
  plans?: Plan[]
  total: number
  hasPaymentMethod: boolean
  currentPage?: number
  links?: PaginationMetaLink[]
  singlePages?: number
  requestPlansList?: (setIsLoading?: (isLoading: boolean) => void) => Promise<void>,
}

interface Props {
  children: JSX.Element
}

// context
export const PlanContext = createContext<PlanContextProps>({
  plans: [],
  total: 0,
  hasPaymentMethod: false,
  singlePages: 0,
});

// provider
const PlanProvider = ({ children }: Props) => {
  const [planList, setPlanList] = useState<Plan[]>([]);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [links, setLinks] = useState(null);
  const [singlePages, setSinglePages] = useState(0);

  const requestPlansList = useCallback((setIsLoading?: (isLoading: boolean) => void) => planAPI.get()
    .then((res) => {
      if (res == null || res.data == null) {
        return;
      }

      setPlanList(res.data.data.products);
      setHasPaymentMethod(res.data.data.hasPaymentMethod);
      setTotal(res.data.meta?.total);
      setLinks(res.data.meta?.links);
      setCurrentPage(res.data.meta?.current_page);
      setSinglePages(res.data.data.singlePages);
    }).finally(() => {
      if (setIsLoading) {
        setIsLoading(false);
      }
    }), []);

  const value = useMemo<PlanContextProps>(() => ({
    plans: planList,
    requestPlansList,
    total,
    hasPaymentMethod,
    currentPage,
    links,
    singlePages,
  }), [planList, requestPlansList, hasPaymentMethod, total, currentPage, links, singlePages]);

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};

export default PlanProvider;
